import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { merge, NEVER, of, tap } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { AccountSelectors, CurrentUserEntityActions } from '@core/account-data';
import { filterDefined, PageVisibilityService } from '@core/common-legacy';
import { bufferUntil, fromChannel, AdminSocket } from '@core/websocket-legacy';

import { AbilitiesActions } from './abilities.store';

@Injectable({
  providedIn: 'root',
})
export class AbilitiesWebsocketService {
  private readonly adminSocket = inject(AdminSocket);
  private readonly store = inject(Store);
  private readonly pageVisibilityService = inject(PageVisibilityService);

  public init(): void {
    this.store
      .select(AccountSelectors.currentCompanyWithUser)
      .pipe(filterDefined)
      .subscribe(({ currentCompany, currentUser }) =>
        this.subscribeToWsMessages(currentCompany!.id, currentUser.id),
      );
  }

  private subscribeToWsMessages(companyId: number, userId: number) {
    const pageVisibleTrigger = () =>
      this.pageVisibilityService.isPageVisible()
        ? of(null)
        : this.pageVisibilityService.$onPageVisible;

    this.adminSocket.ready$
      .pipe(
        switchMap(() => this.adminSocket.subscribeToChannel(`admin:${companyId}:${userId}`)),
        catchError(err => {
          console.error(err);
          return NEVER;
        }),
        switchMap(channel =>
          merge(
            fromChannel(channel, 'role-updated').pipe(
              bufferUntil(pageVisibleTrigger),
              tap(() => {
                this.store.dispatch(CurrentUserEntityActions.fetch());
                this.store.dispatch(AbilitiesActions.roleUpdated());
              }),
            ),
            fromChannel(channel, 'membership-updated').pipe(
              bufferUntil(pageVisibleTrigger),
              tap(() => {
                this.store.dispatch(CurrentUserEntityActions.fetch());
                this.store.dispatch(AbilitiesActions.membershipUpdated());
              }),
            ),
          ),
        ),
      )
      .subscribe();
  }
}
